import { useState, type FormEvent } from "react";
import {
  X,
  User,
  Shield,
  Building2,
  KeyRound,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  Lock,
} from "lucide-react";
import { changePassword, type CurrentUser, ApiError } from "../lib/api";

type UserProfileModalProps = {
  isOpen: boolean;
  onClose: () => void;
  token: string;
  user: CurrentUser;
  onPasswordChanged?: () => void;
};

export function UserProfileModal({
  isOpen,
  onClose,
  token,
  user,
  onPasswordChanged,
}: UserProfileModalProps) {
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile");

  // Change Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (newPassword.length < 8) {
      setErrorMessage("New password must be at least 8 characters long.");
      return;
    }
    if (!/[a-z]/.test(newPassword) || !/[A-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setErrorMessage("New password must include uppercase, lowercase, and numeric characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMessage("New password and confirmation password do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await changePassword(token, currentPassword, newPassword);
      setSuccessMessage("Password successfully updated. You can now use your new password.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      if (onPasswordChanged) {
        onPasswordChanged();
      }
    } catch (err) {
      setErrorMessage(
        err instanceof ApiError ? err.message : "Failed to update password. Please check your current password."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card max-w-lg w-full p-6 relative shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-800 font-extrabold text-sm flex items-center justify-center border border-emerald-300 shadow-2xs">
              {user.username.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 leading-tight">{user.username}</h2>
              <span className="text-xs text-slate-500 font-mono capitalize">{user.role} Account</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 mt-4">
          <button
            onClick={() => {
              setActiveTab("profile");
              setErrorMessage(null);
              setSuccessMessage(null);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-all ${
              activeTab === "profile"
                ? "border-emerald-600 text-emerald-700 bg-emerald-50/50"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <User className="w-4 h-4" />
            <span>Profile Overview</span>
          </button>
          <button
            onClick={() => {
              setActiveTab("password");
              setErrorMessage(null);
              setSuccessMessage(null);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold border-b-2 transition-all ${
              activeTab === "password"
                ? "border-emerald-600 text-emerald-700 bg-emerald-50/50"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <KeyRound className="w-4 h-4" />
            <span>Security & Password</span>
          </button>
        </div>

        {/* Tab 1: Profile Overview */}
        {activeTab === "profile" && (
          <div className="py-5 space-y-4 text-xs">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200/80">
                <span className="text-[11px] font-semibold text-slate-400 block mb-1">Username</span>
                <span className="font-bold text-slate-800 text-sm">{user.username}</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200/80">
                <span className="text-[11px] font-semibold text-slate-400 block mb-1">Assigned Role</span>
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-bold uppercase tracking-wider text-[10px] bg-purple-100 text-purple-800 border border-purple-200">
                  <Shield className="w-3 h-3" />
                  {user.role}
                </span>
              </div>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200/80">
              <span className="text-[11px] font-semibold text-slate-400 block mb-1">Organization / Tenant</span>
              <div className="flex items-center gap-2 font-semibold text-slate-800">
                <Building2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>{user.tenant_name || "Global Fleet (Superadmin Realm)"}</span>
              </div>
            </div>

            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200/80">
              <span className="text-[11px] font-semibold text-slate-400 block mb-1">Account ID</span>
              <span className="font-mono text-slate-600 text-[11px] select-all">{user.id}</span>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setActiveTab("password")}
                className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
              >
                <KeyRound className="w-3.5 h-3.5" />
                <span>Change Password</span>
              </button>
            </div>
          </div>
        )}

        {/* Tab 2: Security & Change Password */}
        {activeTab === "password" && (
          <form onSubmit={handlePasswordSubmit} className="py-4 space-y-4">
            {errorMessage && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-xs rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
                <span>{errorMessage}</span>
              </div>
            )}

            {successMessage && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-lg flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
                <span>{successMessage}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Current Password *</label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter your current password"
                  className="modern-input w-full text-xs pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">New Password *</label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Minimum 8 chars with uppercase, lowercase, & numbers"
                  className="modern-input w-full text-xs pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <span className="text-[11px] text-slate-400 mt-1 block">
                Must contain at least 8 characters, 1 uppercase letter, 1 lowercase letter, and 1 digit.
              </span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm New Password *</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter new password"
                className="modern-input w-full text-xs"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary text-xs py-2 px-3"
                disabled={isSubmitting}
              >
                Close
              </button>
              <button
                type="submit"
                className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
                disabled={isSubmitting}
              >
                <Lock className="w-3.5 h-3.5" />
                <span>{isSubmitting ? "Updating..." : "Update Password"}</span>
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
